import './PayInvoiceSheet.sass';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentUserId, usePayFormTranslations, useTranslator } from '$hooks';
import { useDispatch, useSelector } from 'react-redux';
import { balancePayActions, balancePaySelector } from '$store/balancePay';
import { classNames as cn, formatCurrency, payFormPatch, statReachGoal } from '$utils';
import { balanceActions, balanceSelector } from '$store/balance';
import { BottomBar, Button, Header, InfoBlock } from '$uikit';
import { Loader, PaymentFailed } from '$shared/components';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { payActions, paySelector } from '$store/pay';
import { PayInvoiceSheetProps } from '$core/PayInvoiceSheet/PayInvoiceSheet.interface';
import { WIKI_SUPPORT } from '$shared/constants/wikiPages';
import { useWikiLinkCallback } from '$hooks/useWikiLink';
import { CurrencyEnum } from '$store/models';
import { BalancePayAction } from '$store/balancePay/interface';

export const PayInvoiceSheetInner: FC<PayInvoiceSheetProps> = ({ token }) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const currentUserId = useCurrentUserId();
  const balancePay = useSelector(balancePaySelector);
  const { action, topUp } = useSelector(balanceSelector);
  const pay = useSelector(paySelector);
  const translations = usePayFormTranslations(
    action === BalancePayAction.TRIAL_SUBSCRIPTION_ADULT,
  );
  const [isFormReady, setFormReady] = useState(false);
  const frame = useRef<any>();
  const wikiSupportLink = useWikiLinkCallback(WIKI_SUPPORT);

  useEffect(() => {
    if (balancePay.action) {
      dispatch(balancePayActions.saveContext());
    }
  }, [balancePay]);

  useEffect(() => {
    if (token && frame.current) {
      frame.current.contentWindow.addEventListener('DOMContentLoaded', () => {
        const PaymentForm = frame.current.contentWindow.SmglPaymentForm;
        const paymentForm = new PaymentForm(token, translations);
        paymentForm.onReady = () => {
          setFormReady(true);
          payFormPatch(frame?.current.contentWindow);
        };
        paymentForm.onFailed = (e) => {
          dispatch(payActions.setIsFailed(true));
          statReachGoal('pay_form_error');
        };
        paymentForm.onPaymentStart = () => {
          statReachGoal('payment_start');
        };
        paymentForm.onPaymentSuccess = () => {
          dispatch(balanceActions.updateAllBalances());
          dispatch(payActions.setInvoiceToken(null));
        };
        paymentForm.render();
      });
      statReachGoal('pay_form');
    }
  }, [frame.current, token]);

  const handleClose = useCallback(() => {
    dispatch(payActions.setInvoiceToken(null));
  }, []);

  return (
    <BottomSheet
      scroll={false}
      className={cn('PayInvoiceSheet', {
        failed: pay.isFailed,
      })}
      title={
        action === BalancePayAction.QR_BONUS
          ? t('paywall_trial_limited_title', { price: 1 })
          : action === BalancePayAction.TRIAL_SUBSCRIPTION_ADULT
          ? t('paywall_trial_limited_title', {
              price: !!currentUserId && currentUserId % 2 === 1 ? 1 : 1,
            })
          : action === BalancePayAction.ATTACH_CARD
          ? t('pay_attach_card')
          : topUp.amount > 0
          ? t('pay_sheet_title', {
              amount: formatCurrency(topUp.amount, CurrencyEnum.EUR),
            })
          : 'Pay'
      }
      onClose={handleClose}
    >
      {pay.isFailed && (
        <>
          <PaymentFailed />
          <BottomBar direction="row">
            <Button stretched mode="secondary" onClick={wikiSupportLink}>
              {t('pay_sheet_support_button')}
            </Button>
            <Button
              stretched
              onClick={() => {
                dispatch(balanceActions.topUpRetry());
              }}
            >
              {t('pay_sheet_try_again_button')}
            </Button>
          </BottomBar>
        </>
      )}
      <iframe
        ref={frame}
        src={`/pay_form_v2.html`}
        className={cn('PayInvoiceSheet__iframe', {
          hidden: !isFormReady,
        })}
        frameBorder={0}
      />
      {!isFormReady && <Loader isWrapped />}
    </BottomSheet>
  );
};

export const PayInvoiceSheet: FC = () => {
  const { invoiceToken } = useSelector(paySelector);

  if (!invoiceToken) {
    return null;
  }

  return <PayInvoiceSheetInner token={invoiceToken} />;
};
